import KybPerimeterRepository from "@/core/kyb/repositories/KybPerimeterRepository";
import {
  KybCountryScope,
  KybCountryScopeInput,
  KybDocumentObligation,
  KybDocumentRequirement,
  KybDocumentRequirementInput,
  KybDocumentRequirementTarget,
  KybDraftActivation,
  KybDraftActivationResult,
  KybLegalFormRule,
  KybLegalFormRuleInput,
  KybPerimeterStatus,
  KybPublicationSimulation,
  KybVersionComparison,
  KybReferenceItem,
  KybReferentialVersion,
} from "@/core/kyb/entities/KybPerimeter";
import { RepositoryResult } from "@/core/shared/types/RepositoryResult";
import { HttpClient } from "../../http/HttpClient";
import { ApiResponse } from "../../api/types/ApiResponse";
import { executeKybRequest } from "../../http/services/kybRequest/kybRequest";
import kybPerimeterEndpoints from "../../api/kybPerimeterEndpoints/kybPerimeterEndpoints";
import { incorporationCountriesUrl } from "../../api/requestsBackendEndpoints/requestsBackendEndpoints";

type CountryScopeDto = {
  countryCode: string;
  countryName?: string | null;
  statut: KybPerimeterStatus;
  registreEntreprises?: string | null;
  autoriteFiscale?: string | null;
  centifNationale?: string | null;
  legislationDonnees?: string | null;
  justification?: string | null;
  activatedBy?: string | null;
  activatedAt?: string | null;
  allowsCaseCreation: boolean;
  version: number;
};

type LegalFormDto = {
  formeJuridiqueCode: string;
  formeJuridiqueLabel?: string | null;
  countryCode: string;
  statut: KybPerimeterStatus;
  baseJuridiqueOhada?: string | null;
  organesDirection?: string | null;
  reglesRepresentationLegale?: string | null;
  uboRequired: boolean;
  uboThresholdPercent?: number | null;
  gie: boolean;
  activatedBy?: string | null;
  activatedAt?: string | null;
  allowsCaseCreation: boolean;
  version: number;
};

type RequirementDto = {
  countryCode: string;
  formeJuridiqueCode?: string | null;
  countryDefault: boolean;
  typePieceCode: string;
  typePieceLabel?: string | null;
  obligation: KybDocumentObligation;
  quantiteMin: number;
  baseReglementaire?: string | null;
  commentaire?: string | null;
  statut: KybPerimeterStatus;
  activatedBy?: string | null;
  version: number;
};

type VersionDto = {
  versionId: string;
  countryCode: string;
  numero: number;
  statut: string;
  datePublication?: string | null;
  active: boolean;
  editable: boolean;
  version: number;
};

type SimulationDto = Omit<KybPublicationSimulation, "activeNumero"> & { activeNumero?: number | null };
type CountriesPageDto = { items?: { codeBni?: string | null; nom?: string | null }[] };
type FormesPageDto = { items?: { code: string; libelle?: string | null }[] };

/** Le back rend `null` pour une valeur absente : les écrans travaillent avec des champs optionnels. */
const orUndefined = <T>(value: T | null | undefined): T | undefined => value ?? undefined;

const mapScope = (dto: CountryScopeDto): KybCountryScope => ({
  countryCode: dto.countryCode,
  countryName: orUndefined(dto.countryName),
  status: dto.statut,
  registreEntreprises: orUndefined(dto.registreEntreprises),
  autoriteFiscale: orUndefined(dto.autoriteFiscale),
  centifNationale: orUndefined(dto.centifNationale),
  legislationDonnees: orUndefined(dto.legislationDonnees),
  justification: orUndefined(dto.justification),
  activatedBy: orUndefined(dto.activatedBy),
  activatedAt: orUndefined(dto.activatedAt),
  allowsCaseCreation: dto.allowsCaseCreation,
  version: dto.version,
});

const mapLegalForm = (dto: LegalFormDto): KybLegalFormRule => ({
  formeCode: dto.formeJuridiqueCode,
  formeLabel: orUndefined(dto.formeJuridiqueLabel),
  countryCode: dto.countryCode,
  status: dto.statut,
  baseJuridiqueOhada: orUndefined(dto.baseJuridiqueOhada),
  organesDirection: orUndefined(dto.organesDirection),
  reglesRepresentationLegale: orUndefined(dto.reglesRepresentationLegale),
  uboRequired: dto.uboRequired,
  uboThresholdPercent: orUndefined(dto.uboThresholdPercent),
  gie: dto.gie,
  activatedBy: orUndefined(dto.activatedBy),
  activatedAt: orUndefined(dto.activatedAt),
  allowsCaseCreation: dto.allowsCaseCreation,
  version: dto.version,
});

const mapRequirement = (dto: RequirementDto): KybDocumentRequirement => ({
  countryCode: dto.countryCode,
  formeCode: orUndefined(dto.formeJuridiqueCode),
  countryDefault: dto.countryDefault,
  typePieceCode: dto.typePieceCode,
  typePieceLabel: orUndefined(dto.typePieceLabel),
  obligation: dto.obligation,
  quantiteMin: dto.quantiteMin,
  baseReglementaire: orUndefined(dto.baseReglementaire),
  commentaire: orUndefined(dto.commentaire),
  status: dto.statut,
  activatedBy: orUndefined(dto.activatedBy),
  version: dto.version,
});

const mapVersion = (dto: VersionDto): KybReferentialVersion => ({
  versionId: dto.versionId,
  countryCode: dto.countryCode,
  numero: dto.numero,
  status: dto.statut,
  datePublication: orUndefined(dto.datePublication),
  active: dto.active,
  editable: dto.editable,
  version: dto.version,
});

/** Les champs de texte vides partent en `null` : le back ne garde pas une chaîne vide. */
const textOrNull = (value: string | undefined): string | null => {
  const trimmed = value?.trim();
  return trimmed || null;
};

export default class KybPerimeterRepositoryBackend implements KybPerimeterRepository {
  constructor(private readonly httpClient: HttpClient) {}

  private get<T, R>(url: string, map: (dto: T) => R): Promise<RepositoryResult<R>> {
    return executeKybRequest(() => this.httpClient.get<ApiResponse<T>>({ url, withAuth: true }), map);
  }

  private put<T, R>(url: string, body: unknown, map: (dto: T) => R): Promise<RepositoryResult<R>> {
    return executeKybRequest(() => this.httpClient.put<ApiResponse<T>, unknown>({ url, body, withAuth: true }), map);
  }

  listCountryScopes() {
    return this.get<CountryScopeDto[], KybCountryScope[]>(kybPerimeterEndpoints.countryScopes(), (dtos) => dtos.map(mapScope));
  }

  saveCountryScope(countryCode: string, input: KybCountryScopeInput) {
    const body = {
      registreEntreprises: textOrNull(input.registreEntreprises),
      autoriteFiscale: textOrNull(input.autoriteFiscale),
      centifNationale: textOrNull(input.centifNationale),
      legislationDonnees: textOrNull(input.legislationDonnees),
      justification: textOrNull(input.justification),
      expectedVersion: input.expectedVersion ?? null,
    };
    return this.put<CountryScopeDto, KybCountryScope>(kybPerimeterEndpoints.countryScope(countryCode), body, mapScope);
  }

  activateCountryScope(countryCode: string, expectedVersion: number) {
    return this.put<CountryScopeDto, KybCountryScope>(kybPerimeterEndpoints.activateCountryScope(countryCode), { expectedVersion }, mapScope);
  }

  deactivateCountryScope(countryCode: string, expectedVersion: number) {
    return this.put<CountryScopeDto, KybCountryScope>(kybPerimeterEndpoints.deactivateCountryScope(countryCode), { expectedVersion }, mapScope);
  }

  listLegalForms(countryCode: string) {
    return this.get<LegalFormDto[], KybLegalFormRule[]>(kybPerimeterEndpoints.legalForms(countryCode), (dtos) => dtos.map(mapLegalForm));
  }

  saveLegalForm(countryCode: string, formeCode: string, input: KybLegalFormRuleInput) {
    const body = {
      baseJuridiqueOhada: textOrNull(input.baseJuridiqueOhada),
      organesDirection: textOrNull(input.organesDirection),
      reglesRepresentationLegale: textOrNull(input.reglesRepresentationLegale),
      uboRequired: input.uboRequired,
      uboThresholdPercent: input.uboRequired ? (input.uboThresholdPercent ?? null) : null,
      expectedVersion: input.expectedVersion ?? null,
    };
    return this.put<LegalFormDto, KybLegalFormRule>(kybPerimeterEndpoints.legalForm(countryCode, formeCode), body, mapLegalForm);
  }

  activateLegalForm(countryCode: string, formeCode: string, expectedVersion: number) {
    return this.put<LegalFormDto, KybLegalFormRule>(kybPerimeterEndpoints.activateLegalForm(countryCode, formeCode), { expectedVersion }, mapLegalForm);
  }

  deactivateLegalForm(countryCode: string, formeCode: string, expectedVersion: number) {
    return this.put<LegalFormDto, KybLegalFormRule>(kybPerimeterEndpoints.deactivateLegalForm(countryCode, formeCode), { expectedVersion }, mapLegalForm);
  }

  listRequirements(countryCode: string) {
    return this.get<RequirementDto[], KybDocumentRequirement[]>(kybPerimeterEndpoints.requirements(countryCode), (dtos) => dtos.map(mapRequirement));
  }

  saveRequirement(countryCode: string, target: KybDocumentRequirementTarget, input: KybDocumentRequirementInput) {
    const body = {
      obligation: input.obligation,
      quantiteMin: input.quantiteMin,
      baseReglementaire: textOrNull(input.baseReglementaire),
      commentaire: textOrNull(input.commentaire),
      expectedVersion: input.expectedVersion ?? null,
    };
    return this.put<RequirementDto, KybDocumentRequirement>(kybPerimeterEndpoints.requirement(countryCode, target), body, mapRequirement);
  }

  activateRequirement(countryCode: string, target: KybDocumentRequirementTarget, expectedVersion: number) {
    return this.put<RequirementDto, KybDocumentRequirement>(
      kybPerimeterEndpoints.activateRequirement(countryCode, target),
      { expectedVersion },
      mapRequirement,
    );
  }

  deactivateRequirement(countryCode: string, target: KybDocumentRequirementTarget, expectedVersion: number) {
    return this.put<RequirementDto, KybDocumentRequirement>(
      kybPerimeterEndpoints.deactivateRequirement(countryCode, target),
      { expectedVersion },
      mapRequirement,
    );
  }

  activateDrafts(countryCode: string, drafts: KybDraftActivation) {
    return this.put<{ formsActivated: number; requirementsActivated: number }, KybDraftActivationResult>(
      kybPerimeterEndpoints.activateDrafts(countryCode),
      drafts,
      (dto) => ({ formsActivated: dto.formsActivated, requirementsActivated: dto.requirementsActivated }),
    );
  }

  deleteRequirementDraft(countryCode: string, target: KybDocumentRequirementTarget, expectedVersion: number) {
    return executeKybRequest(
      () =>
        this.httpClient.delete<ApiResponse<null>, undefined>({
          url: kybPerimeterEndpoints.deleteRequirement(countryCode, target, expectedVersion),
          withAuth: true,
        }),
      () => undefined,
    );
  }

  listVersions(countryCode: string) {
    return this.get<VersionDto[], KybReferentialVersion[]>(kybPerimeterEndpoints.versions(countryCode), (dtos) => dtos.map(mapVersion));
  }

  createVersionDraft(countryCode: string) {
    return executeKybRequest(
      () => this.httpClient.post<ApiResponse<VersionDto>, Record<string, never>>({ url: kybPerimeterEndpoints.versionDraft(countryCode), body: {}, withAuth: true }),
      mapVersion,
    );
  }

  discardVersionDraft(versionId: string) {
    return executeKybRequest(
      () =>
        this.httpClient.delete<ApiResponse<null>, undefined>({
          url: kybPerimeterEndpoints.version(versionId),
          withAuth: true,
        }),
      () => undefined,
    );
  }

  compareVersions(fromVersionId: string, toVersionId: string) {
    return this.get<KybVersionComparison, KybVersionComparison>(kybPerimeterEndpoints.compare(fromVersionId, toVersionId), (dto) => dto);
  }

  simulatePublication(versionId: string) {
    return this.get<SimulationDto, KybPublicationSimulation>(kybPerimeterEndpoints.simulate(versionId), (dto) => ({
      ...dto,
      activeNumero: orUndefined(dto.activeNumero),
    }));
  }

  publishVersion(versionId: string, expectedVersion: number) {
    return this.put<VersionDto, KybReferentialVersion>(kybPerimeterEndpoints.publish(versionId), { expectedVersion }, mapVersion);
  }

  listCountries() {
    return this.get<CountriesPageDto, KybReferenceItem[]>(incorporationCountriesUrl(), (page) =>
      (page.items ?? [])
        .filter((country): country is { codeBni: string; nom?: string | null } => Boolean(country.codeBni))
        .map((country) => ({ code: country.codeBni, label: country.nom ?? country.codeBni })),
    );
  }

  listLegalFormReferences() {
    return this.get<FormesPageDto, KybReferenceItem[]>(kybPerimeterEndpoints.legalFormReferences(), (page) =>
      (page.items ?? []).map((forme) => ({ code: forme.code, label: forme.libelle ?? forme.code })),
    );
  }
}
